import React, { useState, useEffect, useRef } from 'react';
import { Button } from '../ui/Button';

const STORAGE_KEY = 'floorplan-company-profile';

/**
 * Load company profile from local storage
 */
export const loadCompanyProfile = () => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (e) {
    console.error('Failed to load company profile:', e);
  }
  return null;
};

/**
 * Save company profile to local storage
 */
export const saveCompanyProfile = (profile) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(profile));
    return true;
  } catch (e) {
    console.error('Failed to save company profile:', e);
    return false;
  }
};

/**
 * CompanyProfileModal - Modal for editing company profile settings
 */
export const CompanyProfileModal = ({ isOpen, onClose }) => {
  const fileInputRef = useRef(null);

  // Form state
  const [companyName, setCompanyName] = useState('');
  const [logo, setLogo] = useState(null); // Base64 string
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [address, setAddress] = useState('');
  const [website, setWebsite] = useState('');
  const [licenseNumber, setLicenseNumber] = useState('');

  const [saved, setSaved] = useState(false);

  // Load saved profile on mount
  useEffect(() => {
    if (isOpen) {
      const profile = loadCompanyProfile();
      if (profile) {
        setCompanyName(profile.companyName || '');
        setLogo(profile.logo || null);
        setPhone(profile.phone || '');
        setEmail(profile.email || '');
        setAddress(profile.address || '');
        setWebsite(profile.website || '');
        setLicenseNumber(profile.licenseNumber || '');
      }
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleLogoUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Check file size (max 500KB for localStorage)
    if (file.size > 500 * 1024) {
      alert('Logo image must be under 500KB');
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      setLogo(event.target.result);
    };
    reader.readAsDataURL(file);
  };

  const handleRemoveLogo = () => {
    setLogo(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSave = () => {
    const profile = {
      companyName,
      logo,
      phone,
      email,
      address,
      website,
      licenseNumber,
    };

    if (saveCompanyProfile(profile)) {
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    }
  };

  const handleClose = () => {
    setSaved(false);
    onClose();
  };

  return (
    <div style={styles.overlay} onClick={handleClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div style={styles.header}>
          <h2 style={styles.title}>Company Profile</h2>
          <Button variant="ghost" size="small" onClick={handleClose} style={{ padding: '4px' }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </Button>
        </div>

        {/* Content - Scrollable */}
        <div style={styles.content}>
          {/* Logo Upload */}
          <div style={styles.field}>
            <label style={styles.label}>Company Logo</label>
            <div style={styles.logoSection}>
              {logo ? (
                <div style={styles.logoPreview}>
                  <img src={logo} alt="Company logo" style={styles.logoImage} />
                  <Button variant="danger" size="small" onClick={handleRemoveLogo}>
                    Remove
                  </Button>
                </div>
              ) : (
                <Button
                  variant="default"
                  onClick={() => fileInputRef.current?.click()}
                  style={{
                    width: '100%',
                    padding: '20px',
                    borderStyle: 'dashed',
                    flexDirection: 'column',
                    gap: '8px',
                  }}
                >
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                    <circle cx="8.5" cy="8.5" r="1.5" />
                    <path d="M21 15l-5-5L5 21" />
                  </svg>
                  <span>Upload Logo</span>
                  <span style={styles.hint}>PNG, JPG (max 500KB)</span>
                </Button>
              )}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/png,image/jpeg,image/jpg"
                onChange={handleLogoUpload}
                style={{ display: 'none' }}
              />
            </div>
          </div>

          {/* Company Name */}
          <div style={styles.field}>
            <label style={styles.label}>Company Name *</label>
            <input
              type="text"
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
              style={styles.input}
              placeholder="Your Company Name"
            />
          </div>

          {/* Phone */}
          <div style={styles.field}>
            <label style={styles.label}>Phone</label>
            <input
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              style={styles.input}
              placeholder="(555) 123-4567"
            />
          </div>

          {/* Email */}
          <div style={styles.field}>
            <label style={styles.label}>Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              style={styles.input}
              placeholder="contact@company.com"
            />
          </div>

          {/* Address */}
          <div style={styles.field}>
            <label style={styles.label}>Address</label>
            <textarea
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              style={styles.textarea}
              placeholder="123 Main Street&#10;City, State 12345"
              rows={2}
            />
          </div>

          {/* Website */}
          <div style={styles.field}>
            <label style={styles.label}>Website</label>
            <input
              type="url"
              value={website}
              onChange={(e) => setWebsite(e.target.value)}
              style={styles.input}
              placeholder="www.company.com"
            />
          </div>

          {/* License Number */}
          <div style={styles.field}>
            <label style={styles.label}>License Number</label>
            <input
              type="text"
              value={licenseNumber}
              onChange={(e) => setLicenseNumber(e.target.value)}
              style={styles.input}
              placeholder="Contractor License #12345"
            />
            <p style={styles.fieldHint}>Often required on estimates and invoices</p>
          </div>

          {/* Preview Section */}
          <div style={styles.previewSection}>
            <label style={styles.label}>Document Preview</label>
            <div style={styles.preview}>
              <div style={styles.previewHeader}>
                {logo && <img src={logo} alt="" style={styles.previewLogo} />}
                <div style={styles.previewInfo}>
                  <div style={styles.previewCompanyName}>
                    {companyName || 'Your Company Name'}
                  </div>
                  {(phone || email) && (
                    <div style={styles.previewContact}>
                      {phone && <span>{phone}</span>}
                      {phone && email && <span style={styles.previewSeparator}>|</span>}
                      {email && <span>{email}</span>}
                    </div>
                  )}
                  {address && (
                    <div style={styles.previewAddress}>
                      {address.split('\n').map((line, i) => (
                        <span key={i}>{line}{i < address.split('\n').length - 1 && <br />}</span>
                      ))}
                    </div>
                  )}
                  {website && (
                    <div style={styles.previewWebsite}>{website}</div>
                  )}
                  {licenseNumber && (
                    <div style={styles.previewLicense}>{licenseNumber}</div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div style={styles.footer}>
          <Button variant="default" onClick={handleClose} style={{ flex: 1, padding: '12px' }}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSave}
            style={{ flex: 1, padding: '12px', background: saved ? '#00ff88' : undefined }}
          >
            {saved ? 'Saved!' : 'Save Profile'}
          </Button>
        </div>
      </div>
    </div>
  );
};

const styles = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 10000,
    padding: '20px',
  },
  modal: {
    backgroundColor: '#1a2332',
    borderRadius: '12px',
    width: '100%',
    maxWidth: '480px',
    maxHeight: '90vh',
    display: 'flex',
    flexDirection: 'column',
    border: '1px solid rgba(255,255,255,0.1)',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '16px 20px',
    borderBottom: '1px solid rgba(255,255,255,0.1)',
    flexShrink: 0,
  },
  title: {
    margin: 0,
    fontSize: '18px',
    fontWeight: '600',
    color: '#fff',
  },
  closeButton: {
    background: 'none',
    border: 'none',
    color: '#6080a0',
    cursor: 'pointer',
    padding: '4px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  content: {
    padding: '20px',
    overflowY: 'auto',
    flex: 1,
  },
  field: {
    marginBottom: '16px',
  },
  label: {
    display: 'block',
    fontSize: '12px',
    color: '#6080a0',
    marginBottom: '6px',
    fontWeight: '500',
  },
  input: {
    width: '100%',
    padding: '10px 12px',
    backgroundColor: 'rgba(255,255,255,0.05)',
    border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: '6px',
    color: '#fff',
    fontSize: '14px',
    outline: 'none',
    boxSizing: 'border-box',
  },
  textarea: {
    width: '100%',
    padding: '10px 12px',
    backgroundColor: 'rgba(255,255,255,0.05)',
    border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: '6px',
    color: '#fff',
    fontSize: '14px',
    outline: 'none',
    boxSizing: 'border-box',
    resize: 'none',
    fontFamily: 'inherit',
  },
  fieldHint: {
    margin: '4px 0 0 0',
    fontSize: '11px',
    color: '#6080a0',
  },
  logoSection: {
    marginTop: '4px',
  },
  logoPreview: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  logoImage: {
    width: '80px',
    height: '80px',
    objectFit: 'contain',
    borderRadius: '8px',
    backgroundColor: 'rgba(255,255,255,0.05)',
    padding: '8px',
  },
  removeLogoButton: {
    padding: '8px 12px',
    backgroundColor: 'transparent',
    border: '1px solid rgba(255, 100, 100, 0.3)',
    borderRadius: '6px',
    color: '#ff6464',
    fontSize: '12px',
    cursor: 'pointer',
  },
  uploadButton: {
    width: '100%',
    padding: '20px',
    backgroundColor: 'rgba(255,255,255,0.02)',
    border: '2px dashed rgba(255,255,255,0.15)',
    borderRadius: '8px',
    color: '#6080a0',
    fontSize: '13px',
    cursor: 'pointer',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '8px',
  },
  hint: {
    fontSize: '11px',
    color: '#4a5a6a',
  },
  previewSection: {
    marginTop: '24px',
    paddingTop: '20px',
    borderTop: '1px solid rgba(255,255,255,0.1)',
  },
  preview: {
    backgroundColor: '#fff',
    borderRadius: '8px',
    padding: '16px',
    marginTop: '8px',
  },
  previewHeader: {
    display: 'flex',
    gap: '12px',
    alignItems: 'flex-start',
  },
  previewLogo: {
    width: '60px',
    height: '60px',
    objectFit: 'contain',
  },
  previewInfo: {
    flex: 1,
  },
  previewCompanyName: {
    fontSize: '16px',
    fontWeight: '700',
    color: '#1a2332',
    marginBottom: '4px',
  },
  previewContact: {
    fontSize: '11px',
    color: '#4a5a6a',
    marginBottom: '2px',
  },
  previewSeparator: {
    margin: '0 6px',
    color: '#ccc',
  },
  previewAddress: {
    fontSize: '11px',
    color: '#4a5a6a',
    lineHeight: '1.3',
  },
  previewWebsite: {
    fontSize: '11px',
    color: '#00c8ff',
    marginTop: '2px',
  },
  previewLicense: {
    fontSize: '10px',
    color: '#6080a0',
    marginTop: '4px',
    fontStyle: 'italic',
  },
  footer: {
    display: 'flex',
    gap: '12px',
    padding: '16px 20px',
    borderTop: '1px solid rgba(255,255,255,0.1)',
    flexShrink: 0,
  },
  cancelButton: {
    flex: 1,
    padding: '12px',
    backgroundColor: 'transparent',
    border: '1px solid rgba(255,255,255,0.2)',
    borderRadius: '6px',
    color: '#8899aa',
    fontSize: '14px',
    cursor: 'pointer',
  },
  saveButton: {
    flex: 1,
    padding: '12px',
    backgroundColor: '#00c8ff',
    border: 'none',
    borderRadius: '6px',
    color: '#000',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
  },
  savedButton: {
    flex: 1,
    padding: '12px',
    backgroundColor: '#00ff88',
    border: 'none',
    borderRadius: '6px',
    color: '#000',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
  },
};

export default CompanyProfileModal;
